import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MapEditLock} from '../../../../shared/models/MapEditLock.model';
import {MapEditLockStompService} from '../../../../core/data-services/map-edit-lock-stomp.service';
import {interval, Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AuthenticationService} from '../../../../core/services/authentication.service';

/*
 * A code-only component to handle the logic associated with the map edit lock requests
 */
@Component({
  template: '',
  selector: 'emap-map-edit-lock'
})
export class MapEditLockComponent implements OnInit, OnDestroy {

  @Input() mapsManagerVersionId: number;

  @Output() lockClaimed: EventEmitter<MapEditLock> = new EventEmitter<MapEditLock>();
  @Output() lockLost: EventEmitter<MapEditLock & {notify?: boolean}> = new EventEmitter<MapEditLock & {notify?: boolean}>();

  private lockHeartbeatSubscription$: Subscription;
  private destroy$ = new Subject<boolean>();

  private readonly LOCK_HEARTBEAT_INTERVAL = 30; // in seconds

  constructor(
      private stompService: MapEditLockStompService,
      private authenticationService: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.initWebsocketClient();
    this.activateLock();

    this.lockLost.subscribe(() => {
      this.stompService.disconnect();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    if (this.stompService) {
      this.stompService.publish('/release', { mapVersionId: this.mapsManagerVersionId });
    }
  }

  public activateLock(forceClaim?: boolean) {
    this.initWebsocketClient(forceClaim);
    this.stompService.stompClient.activate();
  }

  private initWebsocketClient(forceClaim?: boolean) {
    const initialMsgPath = forceClaim ? '/claim' : '/isLocked';
    const token = this.authenticationService.getTokenInfo().accessToken;
    this.stompService.configure('connectHeaders', {
      Authorization: token,
      'map-version-id': this.mapsManagerVersionId
    });

    this.stompService.stompClient.onConnect = () => {
      // Message subscriptions
      this.stompService.subscribe('/claimed', (body: MapEditLock) => {
        if (body) {
          this.lockClaimed.next(body);
          this.initLockHeartbeat();
        }
      });
      this.stompService.subscribe('/isLocked', (body: MapEditLock) => {
        if (body && body.userId) {
          this.lockLost.next(body);
        } else {
          this.stompService.publish(`/claim`);
        }
      });
      this.stompService.subscribe('/lost', (body: MapEditLock) => {
        if (body && body.mapVersionId === this.mapsManagerVersionId) {
          this.lockLost.next({...body, notify: true});
          this.lockHeartbeatSubscription$.unsubscribe();
          this.lockHeartbeatSubscription$ = null;
        }
      });

      // Publish initial message to check if map is locked or claim the lock
      this.stompService.publish(initialMsgPath);
    };

    // Release lock when client disconnects
    // (probably unnecessary because server should handle the disconnect event, but good for redundancy)
    this.stompService.stompClient.onDisconnect = () => {
      this.stompService.publish('/release');
    };
  }

  private initLockHeartbeat() {
    if (this.lockHeartbeatSubscription$) {
      this.lockHeartbeatSubscription$.unsubscribe();
      this.lockHeartbeatSubscription$ = null;
    }

    this.lockHeartbeatSubscription$ = interval(this.LOCK_HEARTBEAT_INTERVAL * 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!document.hidden) {
            this.stompService.publish('/heartbeat');
          }
        });
  }

}
